// Camera Compatibility Probe (iter-37).
//
// A one-shot diagnostic the user can run before starting a session to
// answer "will my phone / webcam / laptop actually work". Returns a
// structured report of:
//   * HTTPS  — is the origin secure enough for getUserMedia to work
//   * API    — is navigator.mediaDevices.getUserMedia present
//   * Grant  — did the user grant permission (probed by attempting a
//              tiny stream)
//   * Cameras — how many videoinput devices does the browser see
//   * Winner  — the actual deviceId + settings we'd use
//
// This is intentionally NOT run automatically on page load — some browsers
// treat unsolicited getUserMedia as a red flag. User taps a button.
export async function probeCamera() {
  const report = {
    https: false,
    api: false,
    permission: null,   // 'granted' | 'denied' | 'prompt' | 'unknown'
    cameras: 0,
    winner: null,       // {deviceId, label, width, height, facingMode}
    error: null,
    tips: [],
  };

  // 1. HTTPS check
  const isSecure =
    typeof window === "undefined" ||
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  report.https = isSecure;
  if (!isSecure) {
    report.tips.push("Open this app via https://. Cameras are blocked on http:// for security.");
  }

  // 2. API presence
  const hasApi = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  report.api = hasApi;
  if (!hasApi) {
    report.tips.push("Update to a recent Chrome, Safari, Firefox, or Edge. Older browsers lack the camera API.");
    report.error = "API missing";
    return report;
  }

  // 3. Best-effort Permissions API check (Safari <16 lacks navigator.permissions.query for camera)
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const p = await navigator.permissions.query({ name: "camera" });
      report.permission = p.state;
    } else {
      report.permission = "unknown";
    }
  } catch {
    report.permission = "unknown";
  }

  if (!isSecure) return report;

  // 4. Attempt to open a camera and read settings
  let stream = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    const track = stream.getVideoTracks()[0];
    const settings = track?.getSettings?.() || {};
    report.winner = {
      deviceId: settings.deviceId || null,
      label: track?.label || null,
      width: settings.width || null,
      height: settings.height || null,
      facingMode: settings.facingMode || null,
    };
    if (report.permission !== "granted") report.permission = "granted";
  } catch (e) {
    const name = e?.name || "unknown";
    report.error = name;
    if (name === "NotAllowedError" || name === "SecurityError") {
      report.permission = "denied";
      report.tips.push("Camera permission is blocked. Click the camera icon in your browser's address bar and change it to Allow.");
    } else if (name === "NotFoundError" || name === "OverconstrainedError") {
      report.tips.push("No camera detected. Plug in a webcam or check that your camera isn't disabled in system Privacy settings.");
    } else if (name === "NotReadableError" || name === "TrackStartError") {
      report.tips.push("Another app is using the camera. Close Zoom, Meet, Teams, or OBS and try again.");
    } else {
      report.tips.push(`Camera failed to start (${name}). Try restarting your browser.`);
    }
  } finally {
    if (stream) stream.getTracks().forEach((t) => t.stop());
  }

  // 5. Enumerate — labels only appear after a successful grant
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    report.cameras = devices.filter((d) => d.kind === "videoinput").length;
    if (report.cameras > 1) {
      report.tips.push(
        `${report.cameras} cameras detected. Use the picker in the top bar of the capture screen to choose.`
      );
    }
  } catch {
    // enumeration is best-effort
  }

  return report;
}

// Human-readable overall verdict for the UI.
export function verdictFor(report) {
  if (!report.https) return { level: "block", label: "NOT SECURE" };
  if (!report.api) return { level: "block", label: "UNSUPPORTED BROWSER" };
  if (report.permission === "denied") return { level: "block", label: "PERMISSION DENIED" };
  if (report.error && !report.winner) return { level: "warn", label: "CAMERA FAILED TO START" };
  if (report.winner) return { level: "ok", label: "READY TO CAPTURE" };
  return { level: "warn", label: "UNKNOWN" };
}
